import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '../../../components/Layout';
import CategoryDropdown from '../../../components/CategoryDropdown';
import { apiFetch } from '../../../utils/api';
import { formatCurrency, formatNumber } from '../../../utils/formatters';
import { useI18n } from '@/hooks/useI18n';
import {
    ExclamationTriangleIcon,
    ClockIcon,
    ArchiveBoxIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    TruckIcon,
    ArchiveBoxArrowDownIcon,
    ListBulletIcon
} from '@heroicons/react/24/outline';

interface InventoryOverviewItem {
    master_ingredient_id: string;
    master_ingredient_name: string;
    product_id: string;
    product_name: string;
    manufacturer?: string;
    cost_center_id: string;
    location_name: string;
    current_quantity: number;
    reserved_quantity: number;
    available_quantity: number;
    average_unit_cost?: number;
    total_value?: number;
    minimum_stock?: number;
    is_low_stock: boolean;
    expiring_soon: boolean;
    nearest_expiry?: string;
    cost_center_name?: string;
}

interface InventoryOverviewResponse {
    items: InventoryOverviewItem[];
    total_items: number;
    low_stock_count: number;
    expiring_soon_count: number;
    total_value: number;
}

interface Location {
    id: string;
    name: string;
    cost_center_id: string;
}

interface MasterIngredient {
    id: string;
    name: string;
    base_unit: string;
}

export default function InventoryOverviewPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const { t } = useI18n();
    const [overview, setOverview] = useState<InventoryOverviewResponse | null>(null);
    const [locations, setLocations] = useState<Location[]>([]);
    const [masterIngredients, setMasterIngredients] = useState<MasterIngredient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [selectedLocation, setSelectedLocation] = useState<string>('');
    const [selectedMasterIngredient, setSelectedMasterIngredient] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [showLowStockOnly, setShowLowStockOnly] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Build query parameters
            const params = new URLSearchParams();
            if (selectedLocation) params.append('cost_center_id', selectedLocation);
            if (selectedMasterIngredient) params.append('master_ingredient_id', selectedMasterIngredient);
            if (selectedCategory) params.append('cost_category_id', selectedCategory);
            if (showLowStockOnly) params.append('low_stock_only', 'true');

            const [overviewResponse, locationsResponse, masterIngredientsResponse] = await Promise.all([
                apiFetch(`/api/inventory/overview/?${params.toString()}`),
                apiFetch('/api/cost-centers/?is_active=true&limit=100'),
                apiFetch('/api/inventory/master-ingredients/')
            ]);

            if (overviewResponse.ok && locationsResponse.ok && masterIngredientsResponse.ok) {
                const overviewData = await overviewResponse.json();
                const locationsData = await locationsResponse.json();
                const masterIngredientsData = await masterIngredientsResponse.json();

                setOverview(overviewData);
                setLocations(locationsData);
                setMasterIngredients(masterIngredientsData);
            } else {
                throw new Error('Failed to fetch inventory data');
            }
        } catch (err) {
            console.error('Error fetching inventory data:', err);
            setError('Failed to load inventory data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedLocation, selectedMasterIngredient, selectedCategory, showLowStockOnly]);

    // Filter items based on search term
    const filteredItems = overview?.items.filter(item =>
        item.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.master_ingredient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.cost_center_name?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <p className="mt-1 text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{t('inventory.title', 'common')}</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            {t('inventory.subtitle', 'common')}
                        </p>
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => navigate({ to: '/inventory/batches'})}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <ArchiveBoxArrowDownIcon className="h-4 w-4 mr-2" />
                            {t('inventory.batches', 'common')}
                        </button>
                        <button
                            onClick={() => navigate({ to: '/inventory/batches/receive-shipment'})}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <TruckIcon className="h-4 w-4 mr-2" />
                            {t('inventory.receiveShipment', 'common')}
                        </button>
                        <button
                            onClick={() => navigate({ to: '/inventory/new'})}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <PlusIcon className="h-4 w-4 mr-2" />
                            {t('inventory.newEntry', 'common')}
                        </button>
                    </div>
                </div>

                {/* Quick Navigation */}
                <div className="bg-white shadow sm:rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{t('inventory.management', 'common')}</h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <button
                                onClick={() => navigate({ to: '/inventory/batches'})}
                                className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <div className="flex-shrink-0">
                                    <ArchiveBoxArrowDownIcon className="h-6 w-6 text-gray-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="absolute inset-0" aria-hidden="true" />
                                    <p className="text-sm font-medium text-gray-900">{t('inventory.inventoryBatches', 'common')}</p>
                                    <p className="text-sm text-gray-500">{t('inventory.batchesDescription', 'common')}</p>
                                </div>
                            </button>

                            <button
                                onClick={() => navigate({ to: '/inventory/batches/receive-shipment'})}
                                className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <div className="flex-shrink-0">
                                    <TruckIcon className="h-6 w-6 text-gray-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="absolute inset-0" aria-hidden="true" />
                                    <p className="text-sm font-medium text-gray-900">{t('inventory.receiveShipment', 'common')}</p>
                                    <p className="text-sm text-gray-500">{t('inventory.receiveShipmentDescription', 'common')}</p>
                                </div>
                            </button>

                            <button
                                onClick={() => navigate({ to: '/inventory/transactions'})}
                                className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <div className="flex-shrink-0">
                                    <ListBulletIcon className="h-6 w-6 text-gray-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="absolute inset-0" aria-hidden="true" />
                                    <p className="text-sm font-medium text-gray-900">{t('inventory.transactions', 'common')}</p>
                                    <p className="text-sm text-gray-500">{t('inventory.transactionsDescription', 'common')}</p>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                {overview && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <ArchiveBoxIcon className="h-6 w-6 text-gray-400" />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">{t('inventory.totalItems', 'common')}</dt>
                                            <dd className="text-lg font-medium text-gray-900">{overview.total_items}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">{t('inventory.lowStock', 'common')}</dt>
                                            <dd className="text-lg font-medium text-gray-900">{overview.low_stock_count}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <ClockIcon className="h-6 w-6 text-red-400" />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">{t('inventory.expiringsoon', 'common')}</dt>
                                            <dd className="text-lg font-medium text-gray-900">{overview.expiring_soon_count}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <span className="text-green-400 text-xl">$</span>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">{t('inventory.totalValue', 'common')}</dt>
                                            <dd className="text-lg font-medium text-gray-900">{formatCurrency(overview.total_value)}</dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white shadow rounded-lg p-6">
                    <div className="flex items-center space-x-4">
                        <div className="flex-1">
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={t('inventory.searchPlaceholder', 'common')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                                />
                            </div>
                        </div>

                        <select
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">{t('inventory.allLocations', 'common')}</option>
                            {locations.map((location) => (
                                <option key={location.id} value={location.id}>
                                    {location.name}
                                </option>
                            ))}
                        </select>

                        <select
                            value={selectedMasterIngredient}
                            onChange={(e) => setSelectedMasterIngredient(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">{t('inventory.allIngredients', 'common')}</option>
                            {masterIngredients.map((ingredient) => (
                                <option key={ingredient.id} value={ingredient.id}>
                                    {ingredient.name}
                                </option>
                            ))}
                        </select>

                        <CategoryDropdown
                            selectedCategoryId={selectedCategory}
                            onCategoryChange={(categoryId: string | null) => setSelectedCategory(categoryId || '')}
                            placeholder={t('inventory.allCategories', 'common')}
                            className="min-w-[200px]"
                        />

                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={showLowStockOnly}
                                onChange={(e) => setShowLowStockOnly(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">{t('inventory.lowStockOnly', 'common')}</span>
                        </label>
                    </div>
                </div>

                {/* Inventory Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('inventory.product', 'common')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('inventory.costCenter', 'common')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('inventory.stock', 'common')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('inventory.value', 'common')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('inventory.status', 'common')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredItems.map((item) => (
                                    <tr key={`${item.product_id}-${item.cost_center_id}`} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{item.product_name}</div>
                                                {item.manufacturer && (
                                                    <div className="text-sm text-gray-500">{item.manufacturer}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {item.cost_center_name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {formatNumber(item.available_quantity)}
                                            </div>
                                            {item.reserved_quantity > 0 && (
                                                <div className="text-xs text-gray-500">
                                                    ({formatNumber(item.reserved_quantity)} {t('inventory.reserved', 'common')})
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {item.total_value ? formatCurrency(item.total_value) : '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex space-x-2">
                                                {item.is_low_stock && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                                        {t('inventory.lowStockBadge', 'common')}
                                                    </span>
                                                )}
                                                {item.expiring_soon && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        <ClockIcon className="h-3 w-3 mr-1" />
                                                        {t('inventory.expiringBadge', 'common')}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {filteredItems.length === 0 && (
                        <div className="text-center py-12">
                            <ArchiveBoxIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('inventory.noItems', 'common')}</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {searchTerm || selectedLocation || selectedMasterIngredient || showLowStockOnly
                                    ? t('inventory.noItemsFiltered', 'common')
                                    : t('inventory.getStarted', 'common')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/inventory/')({
  component: InventoryOverviewPage,
})
